import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { isEmpty } from "lodash";
import moment, { Moment } from "moment";
import Dropzone from "../../util/ui/dropzone";
import { useSnackBar } from "../../../stores/snackBarStores";
import { IconFlagPlus } from "@tabler/icons-react";
import { useUsuario } from "../../../providers/usuarioProvider";
import UploadDb from "../../../db/uploadDb";
import { addDoc } from "firebase/firestore";
import Upload from "../../../models/upload";
import { SituacaoUpload } from "../../../models/enum";
import { getStorage, ref, uploadBytes } from "firebase/storage";
import felixAnalytics from "../../../felixAnalytics";

type Props = {
  open: boolean;
  onClose: (success: boolean) => void;
};

export default function UploadCheckpointDialog({ open, onClose }: Props) {
  const { visualizado } = useUsuario();
  const [data, setData] = useState<Moment | undefined>(undefined);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [carregando, setCarregando] = useState(false);
  const snackBar = useSnackBar();
  const [help, setHelp] = useState<string | undefined>();

  const handleDropAccepted = (files: File[]) => {
    if (
      files.length > 0 &&
      files[0].type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      setArquivo(files[0]);
    } else {
      snackBar({
        type: "ALERT",
        payload: {
          mensagem: "Apenas arquivos .xlsx são permitidos.",
          severidade: "error",
        },
      });
    }
  };

  const handleUpload = async () => {
    if (!!visualizado && !!arquivo) {
      const uploadCollection = new UploadDb().collection({
        usuarioId: visualizado,
        uploadId: "",
      });
      addDoc(
        uploadCollection,
        new Upload({
          id: "",
          nomeArquivoOriginal: arquivo.name,
          situacao: SituacaoUpload.SALVO,
          dataEmissao: data,
          dataUpload: moment(),
        })
      )
        .then((doc) => {
          return uploadBytes(
            ref(getStorage(), `/usuarios/${visualizado}/uploads/${doc.id}`),
            arquivo,
            { customMetadata: { nome: arquivo.name } }
          ).then(() =>
            felixAnalytics.track("posicao_cadastrada", { tipo: "upload" })
          );
        })
        .then(() => onClose(true))
        .catch((error) => {
          snackBar({
            type: "ALERT",
            payload: {
              mensagem:
                "Houve um erro ao carregar o checkpoint. Tente novamente.",
              severidade: "error",
            },
          });
          console.error(error);
        })
        .finally(() => {
          setCarregando(false);
          setData(undefined);
          setArquivo(null);
        });
    }
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          color="text.primary"
        >
          <Box mr={1} display="flex">
            <IconFlagPlus size={16} />
          </Box>
          <Typography variant="h4" color="text.primary">
            Novo Ponto de Verificação
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" alignItems="stretch">
          <Box mb={1}>
            <Dialog
              open={!!help}
              onClose={() => setHelp(undefined)}
              maxWidth="md"
              fullWidth
            >
              <iframe
                src={help}
                width="100%"
                height="640"
                allowFullScreen
                frameBorder="0"
              />
            </Dialog>
            <Alert
              color="info"
              action={
                <Button
                  variant="contained"
                  color="info"
                  onClick={() =>
                    setHelp(
                      "https://scribehow.com/shared/Como_baixar_o_Extrato_de_Posicao_da_B3__GRGj5wSvS52G7C59XczvoQ"
                    )
                  }
                >
                  Baixar
                </Button>
              }
            >
              1. Cadastre um novo Ponto de Verificação importando o Extrato de
              Posição da B3
            </Alert>
          </Box>
          <Typography variant="labelMono" color="text.secondary" mb={0.5}>
            Data da Posição
          </Typography>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              sx={{ width: "18ch" }}
              slotProps={{ textField: { size: "small" } }}
              value={data ?? null}
              onChange={(e) => {
                if (e?.isValid()) setData(e.startOf("day"));
              }}
            />
          </LocalizationProvider>
          <Box py={2} width="100%">
            <Dropzone
              onDropAccepted={handleDropAccepted}
              maxFiles={1}
              acceptedFormats={{ xlsx: true }}
            />
            {arquivo && (
              <Typography
                variant="dataMono"
                color="text.secondary"
                display="block"
                mt={1}
                sx={{ fontSize: 12, wordBreak: "break-all" }}
              >
                Arquivo: {arquivo.name}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)} color="inherit">
          Cancelar
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          disabled={isEmpty(data) || !arquivo}
          loading={carregando}
          onClick={handleUpload}
        >
          Carregar
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
